import { useState } from "react";
import { Link } from "react-router-dom";
import { channels } from "@/mocks/categories";
import { news } from "@/mocks/news";
import { faqItems } from "@/mocks/faq";
import { SectionHeading } from "@/components/feature/UI";
import { siteName, siteSlogan } from "@/hooks/useSeo";

export function CtaSection() {
  return (
    <section className="mx-auto max-w-[1200px] px-4 py-12 md:px-6">
      <div className="relative overflow-hidden rounded-lg bg-primary-600">
        <div className="absolute inset-0 opacity-10" style={{ pointerEvents: "none" }}>
          <i className="ri-flashlight-line absolute -right-6 -top-6 text-[220px] text-background-50"></i>
        </div>
        <div className="relative grid grid-cols-1 items-center gap-6 p-6 md:grid-cols-2 md:p-10">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-md bg-background-50/15 px-3 py-1 text-xs font-medium text-background-50">
              <i className="ri-megaphone-line"></i>
              欢迎投稿
            </span>
            <h2 className="font-display mt-3 text-2xl font-black leading-snug text-background-50 md:text-3xl">
              你有线索，我们来拆解
            </h2>
            <p className="mt-3 text-sm leading-6 text-background-100/85 md:text-base">
              无论你是行业从业者，还是眼尖的网友，只要你握有值得挖掘的新瓜线索，都可以通过站内投稿告诉我们。编辑部会认真核实、谨慎发布。
            </p>
          </div>
          <div className="flex flex-col gap-3 md:items-end">
            <div className="grid grid-cols-3 gap-3 text-center md:mb-2">
              {[
                { v: `${channels.length}`, l: "内容栏目" },
                { v: `${news.length}+`, l: "深度文章" },
                { v: "100%", l: "匿名保护" },
              ].map((s) => (
                <div key={s.l} className="rounded-md bg-background-50/10 px-3 py-3">
                  <p className="font-display text-xl font-black text-accent-300">{s.v}</p>
                  <p className="text-xs text-background-100/70">{s.l}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-accent-200"
              >
                <i className="ri-send-plane-line"></i>
                我要投稿
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-100/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
              >
                了解本站
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FaqPreview() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const preview = faqItems.slice(0, 4);
  return (
    <section className="bg-background-100/70">
      <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6">
        <SectionHeading
          title="关于本站，你可能想问"
          desc="投稿是否安全？报道是否真实？我们整理了一些高频问题，先说清楚再一起理性吃瓜。"
          more={{ to: "/faq", label: "查看全部问答" }}
        />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {preview.map((it, i) => {
            const open = openIdx === i;
            return (
              <div key={it.q} className="rounded-lg border border-background-200/80 bg-background-50">
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? null : i)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                >
                  <span className="flex items-start gap-3">
                    <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold ${open ? "bg-primary-500 text-background-50" : "bg-primary-100 text-primary-700"}`}>
                      {i + 1}
                    </span>
                    <span className="font-display text-sm font-bold leading-snug text-foreground-950 md:text-base">
                      {it.q}
                    </span>
                  </span>
                  <i className={`ri-add-line text-foreground-400 transition-transform ${open ? "rotate-45" : ""}`}></i>
                </button>
                {open && <p className="px-5 pb-5 pl-[52px] text-sm leading-6 text-foreground-600">{it.a}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function AboutTeaser() {
  return (
    <section className="mx-auto flex max-w-[1200px] flex-col items-center px-4 py-12 text-center md:px-6">
      <div className="flex items-center gap-2.5">
        <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
          <i className="ri-flashlight-fill text-xl"></i>
        </span>
        <span className="font-display text-xl font-bold text-foreground-950">{siteName}</span>
      </div>
      <p className="mt-4 max-w-2xl text-sm leading-6 text-foreground-600 md:text-base">
        {siteSlogan}。{siteName}是一档关注娱乐工业与人设现象的独立内容站，聚焦新晋网红、小艺人、练习生与新入行演员的幕后故事。我们不做情绪的放大器，只做真相的搬运工与行业的观察者。
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link
          to="/about"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          关于我们
          <i className="ri-arrow-right-line"></i>
        </Link>
        <Link
          to="/channels"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300"
        >
          开始吃瓜
          <i className="ri-flashlight-line text-primary-500"></i>
        </Link>
      </div>
    </section>
  );
}